import React from 'react';
import { TableHead, TableRow, TableCell, Checkbox, makeStyles } from '@material-ui/core';
import SortDownIcon from '@axes4/react-icons/jsx/SortDown';
import SortUpIcon from '@axes4/react-icons/jsx/SortUp';
import { EnhancedTableData, EnhancedTableColumnDefinition } from './EnhancedTable';

export enum SortOrder {
    ASC = 'ascending',
    DESC = 'descending',
}

const useStyles = makeStyles(theme => ({
    sortableTableHeader: {
        border: 'none',
        background: 'none',
        display: 'flex',
        alignItems: 'center',
        padding: `${theme.spacing(1)}px 0`,
        margin: `-${theme.spacing(1)}px 0`,
        width: '100%',
        fontWeight: 'inherit',
        color: theme.palette.text.primary,
        cursor: 'pointer',
        '&:hover, &:focus': {
            color: theme.palette.text.secondary,
        },
    },
    sortableTableHeaderLabel: {
        display: 'block',
    },
    sortableTableHeaderIcon: {
        marginLeft: theme.spacing(1),
    },
    th: {
        fontWeight: 700,
    },
}));

export interface EnhancedTableHeadProps<T extends EnhancedTableData> {
    selectable: boolean;
    columns: EnhancedTableColumnDefinition<T>[];
    allSelected: boolean;
    selectAll: () => void;
    selectAllLabel?: string;
    selectNone: () => void;
    selectionCount: number;
    sortByIndex: number;
    setSortByIndex: (index: number) => void;
    sortOrder: SortOrder;
    setSortOrder: (order: SortOrder) => void;
    disabled?: boolean;
}

const EnhancedTableHead = <T extends EnhancedTableData>(props: EnhancedTableHeadProps<T>) => {
    const handleSelectAllChange = () => {
        props.allSelected ? props.selectNone() : props.selectAll();
    };
    const classes = useStyles();
    const getCellLabel = cell => Object.prototype.hasOwnProperty.call(cell, 'label') ? cell.label : cell;
    const getSortMode = cellIndex => props.sortByIndex === cellIndex ? props.sortOrder : null;
    const handleSortableHeaderClick = cellIndex => () => {
        if (cellIndex === props.sortByIndex) {
            props.setSortOrder(props.sortOrder === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC);
        } else {
            props.setSortOrder(SortOrder.ASC);
            props.setSortByIndex(cellIndex);
        }
    };
    return (
        <TableHead>
            <TableRow>
                {props.selectable && (
                    <TableCell padding="checkbox">
                        <Checkbox
                            indeterminate={!props.allSelected && props.selectionCount > 0}
                            checked={props.allSelected && props.selectionCount > 0}
                            disabled={props.disabled}
                            onChange={handleSelectAllChange}
                            inputProps={{
                                'aria-label': props.selectAllLabel || 'select all',
                            }}
                        />
                    </TableCell>
                )}
                {props.columns.map((cell, i) => {
                    const sortMode = getSortMode(i);
                    return (
                        <TableCell
                            key={i}
                            className={classes.th}
                        >
                            {cell.sortable
                            ? (
                                <button
                                    className={classes.sortableTableHeader}
                                    aria-sort={sortMode}
                                    onClick={handleSortableHeaderClick(i)}
                                >
                                    <span className={classes.sortableTableHeaderLabel}>
                                        {getCellLabel(cell)}
                                    </span>
                                    {sortMode === SortOrder.ASC && (
                                        <SortUpIcon className={classes.sortableTableHeaderIcon} fontSize="small" focusable="false"/>
                                    )}
                                    {sortMode === SortOrder.DESC && (
                                        <SortDownIcon className={classes.sortableTableHeaderIcon} fontSize="small" focusable="false"/>
                                    )}
                                </button>
                            )
                            : (
                                getCellLabel(cell)
                            )}
                        </TableCell>
                    );
                })}
            </TableRow>
        </TableHead>
    );
};

export {
    EnhancedTableHead,
};
